import { formatMoney, formatPriceWithComparison, responsiveImage } from '../../utils';

const imageSettings = {
  image_size: '310x403',
  image_breakpoints: ['360', '480', '768', '1024', '1200', '1440'],
  image_sizes: ['154x200', '220x286', '360x468', '290x377', '340x442', '310x403'],
};

/**
 * Handle card DOM changes
 * @description Handles product card DOM changes after hover or color click events.
 * @param {HTMLElement} card the product card html element
 * @param {Object} additionalProductData additional product data gotten from a fetch or storage
 */
const productCardDomChange = (card, additionalProductData) => {
  const cardHandle = card.dataset.handle;
  const { handle, title, vendor, imgUrl, imgAlternateUrl, badges, price, compareAtPrice, variation_title } = additionalProductData;
  const imgSecondaryElem = card.querySelector('[data-product-card-image-secondary]');

  imgSecondaryElem.innerHTML = responsiveImage(title, imgAlternateUrl, imageSettings);

  // No need to continue if we're hovering on the initial card
  if (cardHandle === handle) return;

  // Change card handle
  card.dataset.handle = handle;

  // Change primary image
  const imgPrimaryElem = card.querySelector('[data-product-card-image-primary]');
  imgPrimaryElem.innerHTML = responsiveImage(title, imgUrl, imageSettings);

  // Change badges
  const badgesElem = card.querySelector('[data-product-card-badges]');
  badgesElem.innerHTML = unescapeHTML(badges);

  // Change vendor
  const vendorElem = card.querySelector('[data-product-card-vendor]');
  if (variation_title && variation_title !== '') {
    vendorElem.innerHTML = `<span>${vendor}</span> • <span class="weight-regular">${variation_title}</span>`;
  } else {
    vendorElem.innerHTML = `<span>${vendor}</span>`;
  }

  // Change title
  const titleElem = card.querySelector('[data-product-card-title]');
  titleElem.innerHTML = `<span>${title}</span>`;

  // Change price
  const priceElem = card.querySelector('[data-product-card-price]');
  priceElem.innerHTML = `
    <span>
      ${formatPriceWithComparison(price, compareAtPrice)}
    </span>
  `;

  // Change product page urls
  card.querySelectorAll('[data-product-card-link]').forEach((anchor) => {
    anchor.href = `/products/${handle}`;
  });
};

/**
 * Handle card hover
 * @description Handles the hover event for product cards.
 */
export const handleCardHover = async (event) => {
  const elem = event.currentTarget;
  const card = elem.closest('[data-product-card]');
  const productHandle = card?.dataset?.handle;
  const loadingClass = 'product-card--is-loading';

  if (!productHandle) return;

  try {
    card.classList.add(loadingClass);
    const additionalProductData = await fetchAdditionalProductData(productHandle);

    if (!additionalProductData) {
      throw new Error('No additionalProductData returned');
    }

    productCardDomChange(card, additionalProductData);
  } catch (error) {
    console.error('Error getting additional product data:', error);
  } finally {
    card.classList.remove(loadingClass);
  }
};

/**
 * Handle swatch click
 * @description Handles the click event for product card color swatches.
 */
export const handleSwatchClick = async (event) => {
  event.preventDefault();
  event.stopPropagation();

  const elem = event.currentTarget;
  const card = elem.closest('[data-product-card]');
  const swatchProductHandle = elem.dataset?.productCardColorId;
  const loadingClass = 'product-card--is-loading';

  if (!swatchProductHandle) return;

  try {
    card.classList.add(loadingClass);
    const additionalProductData = await fetchAdditionalProductData(swatchProductHandle);

    if (!additionalProductData) {
      throw new Error('No additionalProductData returned');
    }

    const cardSwatches = elem.closest('[data-product-card]').querySelectorAll('[data-product-card-color-id]');
    const activeSwatchClass = 'product-card-colors__link--is-active';

    cardSwatches.forEach((cardSwatch) => cardSwatch.classList.remove(activeSwatchClass));
    elem.classList.add(activeSwatchClass);

    productCardDomChange(card, additionalProductData);
  } catch (error) {
    console.error('Error getting additional product data:', error);
  } finally {
    card.classList.remove(loadingClass);
  }
};

/**
 * Build responsive image
 * @description Builds a responsive image with markup.
 */
const buildResponsiveImage = (settings) => {
  const { image_url, alt } = settings;
  const { image_size, image_breakpoints, image_sizes } = imageSettings;

  // Return fallback image if no image found
  if (!image_url) {
    return `
      <picture>
        <img loading="lazy" alt="No image found" width="310" height="403" src="http://cdn.shopify.com/s/images/admin/no-image-compact.gif" />
      </picture>
    `;
  }

  // Build image
  const image = `
    <img
      loading="lazy"
      alt="${alt}"
      width="${image_url.split('x')[0]}"
      height="${image_url.split('x')[1]}"
      src="${image_url.replace(/\/(.*)\.(\w{2,4})/g, '/$1_' + image_size + '_crop_center.$2')}"
      srcset="${image_url.replace(/\/(.*)\.(\w{2,4})/g, '/$1_' + image_size + '_crop_center.$2')} 1x, ${image_url.replace(/\/(.*)\.(\w{2,4})/g, '/$1_' + image_size + '_crop_center@2x.$2')} 2x"
    />
  `;

  // Build sources
  const sources = image_sizes.map(
    (size, index) => `
      <source
        media="(max-width: ${image_breakpoints[index]}px)"
        srcset="${image_url.replace(/\/(.*)\.(\w{2,4})/g, '/$1_' + size + '_crop_center.$2')} 1x,
        ${image_url.replace(/\/(.*)\.(\w{2,4})/g, '/$1_' + size + '_crop_center@2x.$2')} 2x"
      />
    `
  );

  // Combine html
  return `
    <picture>
      ${sources}
      ${image}
    </picture>
  `;
};

/**
 * Build product badges
 * @description Builds product badges to append to the  product card.
 */
const buildProductBadges = (item) => {
  const { named_tags, price, compare_at_price, price_ratio, variants_inventory_count } = item;
  const badges = [];

  const badgeTranslations = {
    sold_out: 'Sold out',
    discount: 'Save',
    sustainable: 'Sustainable',
    new: 'New',
    bundle_and_save: 'Bundle and save',
    bestseller: 'Bestseller',
    back_in_stock: 'Back in stock',
  };

  // Sold out badge
  if (variants_inventory_count <= 0) {
    badges.push({
      label: badgeTranslations['sold_out'] || 'Sold out',
      customClass: 'sold-out',
    });
  }

  // Discounted badge
  if (Number(compare_at_price) && Number(compare_at_price) > Number(price)) {
    const salePreText = badgeTranslations['discount'] || 'Save';
    const saleDiscountPercentValue = Math.round((1.0 - price_ratio) * 100);

    badges.push({
      label: `${salePreText} ${saleDiscountPercentValue}%`,
      customClass: 'sale',
    });
  }

  // Custom badges (gotten from named tags)
  let customBadgeTags = named_tags['custom-badge'] || [];

  if (typeof customBadgeTags === 'string') {
    customBadgeTags = [customBadgeTags];
  }

  customBadgeTags.forEach((tag) => {
    const split = tag.split(':');
    const label = split[0]?.trim();
    const translatedLabel = badgeTranslations[label];
    const customClass = split[1]?.trim()?.toLowerCase();

    badges.push({
      label: translatedLabel || label,
      customClass: customClass,
    });
  });

  if (!badges.length) return '';

  const badgesHtml = badges
    .map(({ label, customClass }) => {
      var baseClass = 'product__badge';
      var additionalClass = customClass ? ` ${baseClass}--${customClass}` : '';

      return `
      <div class="${baseClass}${additionalClass}">${label}</div>
    `;
    })
    .join('');

  return `
    <div class="product__badges">
      ${badgesHtml}
    </div>
  `;
};

/**
 * Build color swatches
 * @description Builds color swatches to append to the  product card.
 */
const buildColorSwatches = (colors) => {
  if (!colors.length) return '';

  const { colorLimit } = window.algolia;

  // Build swatches
  const swatches = `
    ${colors
      .map(
        ({ handle, colorLabel, colorHex }, i) => `
        <a
          href="#"
          class="product-card-colors__link ${i === 0 ? ' product-card-colors__link--is-active' : ''}${i >= colorLimit ? ' product-card-colors__link--hidden' : ''}"
          title="${colorLabel}"
          data-product-card-color-id="${handle}"
        >
          <span
            class="product-card-colors__swatch${colorHex ? '' : ' product-card-colors__swatch--fallback'}"
            style="${colorHex ? `background-color: ${colorHex}` : ''}"
          >
            ${colorHex ? '' : colorLabel}
          </span>
        </a>
      `
      )
      .join('')}
    ${
      colors.length > colorLimit
        ? `<div class="product-card-colors__remaining" data-show-remaining-colours>
            +${colors.length - colorLimit} more
          </div>`
        : ''
    }
  `;

  return `
   <div class="product-card-colors is-flex" data-product-card-swatches>
     ${swatches}
   </div>
 `;
};

/**
 * Product Card Template
 * @description The template for product card items.
 */
export const productCardTemplate = (item) => {
  const productImagePrimary = buildResponsiveImage({
    alt: item.title,
    image_url: item.image,
  });

  const productUrl = `/products/${item.handle}`;

  const priceInCents = Number(item.price) * 100;
  const comparePriceInCents = Number(item.compare_at_price) * 100;
  const isComparing = comparePriceInCents && comparePriceInCents > priceInCents;
  const formattedPrice = `
    <span class="${isComparing ? 'text-red' : 'text-primary'}">
      ${formatMoney(priceInCents, window.algolia.moneyFormat)}
    </span>
  `;

  const formattedComparePrice = isComparing
    ? `<span class="text-gray">
        <s>${formatMoney(comparePriceInCents, window.algolia.moneyFormat)}</s>
      </span>`
    : '';

  const productBadges = buildProductBadges(item);
  const productColorSwatches = buildColorSwatches(item.colors);

  return `
    <div
      class="product-card product-card--is-in-searchdropdown"
      data-algolia-index="${item.index}"
      data-algolia-position="${item.productPosition}"
      data-algolia-queryid="${item.queryID}"
      data-algolia-objectid="${item.objectID}"
      data-handle="${item.handle}"
      data-product-card
    >
      <div class="loading-overlay">
        <div class="loading-overlay__spinner">
          <svg class="icon icon-spinner icon--xl" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" viewBox="0 0 66 66">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
      </div>
      <div class="product-card__inner" data-product-card-inner>
        <div class="product-card__image">
          <a
            class="full-unstyled-link"
            href="${productUrl}"
            title="${item.title}"
            data-product-card-link
          >
            <figure class="img img--wrapper img--cover img--portrait product-card__image--primary" data-product-card-image-primary>
              ${productImagePrimary}
            </figure>
            <figure class="img img--wrapper img--cover img--portrait product-card__image--secondary" data-product-card-image-secondary>
            </figure>
          </a>
        </div>
        <div data-product-card-badges>
          ${productBadges}
        </div>
        <a
          class="full-unstyled-link"
          href="${productUrl}"
          title="${item.title}"
          data-product-card-link
        >
          <div class="product-card__information">
            <span class="product-card__vendor weight-medium" data-product-card-vendor>
              ${`<span>${item.vendor}</span>`}
              ${item.meta?.variation?.title && item.meta?.variation?.title !== '' ? ` • <span class="weight-regular">${item.meta?.variation?.title}</span>` : ''}
            </span>
            <span class="product-card__title text-gray" data-product-card-title>
              ${`<span>${item.title}</span>`}
            </span>
          </div>
          <div class="product-card__price" data-product-card-price>
            ${`<span>${formattedPrice}${formattedComparePrice}</span>`}
          </div>
        </a>

        <div
          data-bv-show="inline_rating"
          data-bv-product-id="${item.id}"
          data-bv-redirect-url="${window.location.origin}/products/${item.handle}"
          data-bv-seo="false"></div>
      </div>
      ${productColorSwatches}
    </div>
  `;
};
